import React from "react";
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Icon from "../Icon";

/**
 * Tabela com reordenação por arrasta e solta. Mesma API do <Table> (header +
 * rows), com uma coluna extra de alça (drag handle) no início. Só as linhas com
 * `row.id` são arrastáveis; linhas sem id (ex.: variáveis do sistema) ficam
 * fixas. Ao soltar, chama `onReorder(orderedIds)` com os ids arrastáveis na
 * nova ordem — o pai persiste e atualiza o estado.
 */

const handleCellStyle = {
    width: 40,
    textAlign: 'center',
    color: '#b3b3b3',
    cursor: 'grab',
    touchAction: 'none',
};

// Desliga a animação de acomodação ao soltar. Como reordenamos o estado na
// hora (onReorder), a lista já reordena no DOM; sem isso o item solto anima da
// posição antiga (lá do topo) até a nova. Os vizinhos continuam abrindo espaço
// durante o arraste normalmente — isso é controlado pelo transform, não por aqui.
const animateLayoutChanges = () => false;

const SortableRow = ({ row }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: row.id,
        animateLayoutChanges,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
        zIndex: isDragging ? 5 : undefined,
    };

    return (
        <tr ref={setNodeRef} style={style} className={row.className || ''}>
            <td style={handleCellStyle} {...attributes} {...listeners}>
                <Icon name="drag" />
            </td>
            {row.data.map((item, index) => (
                <td
                    key={index}
                    onClick={() => row.action && row.action()}
                    className={row.action ? 'has-link' : ''}
                >
                    {item}
                </td>
            ))}
        </tr>
    );
};

const StaticRow = ({ row }) => (
    <tr className={row.className || ''}>
        <td style={{ width: 40 }} />
        {row.data.map((item, index) => (
            <td
                key={index}
                onClick={() => row.action && row.action()}
                className={row.action ? 'has-link' : ''}
            >
                {item}
            </td>
        ))}
    </tr>
);

export const SortableTable = ({ header, rows = [], loading, onReorder, minWidth }) => {
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const sortableIds = rows.filter(row => row.id != null).map(row => row.id);

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldIndex = sortableIds.indexOf(active.id);
        const newIndex = sortableIds.indexOf(over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        onReorder(arrayMove(sortableIds, oldIndex, newIndex));
    };

    const colSpan = header.length + 1;

    return (
        <div className="table-responsive">
            <table className="table" style={minWidth != null ? { minWidth } : undefined}>
                <thead>
                    <tr>
                        <th style={{ width: 40 }} />
                        {header.map((item, index) => (
                            <th key={index}>{item}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {loading &&
                        <tr>
                            <td colSpan={colSpan} className="no-data">Carregando...</td>
                        </tr>
                    }
                    {!loading && rows.length === 0 &&
                        <tr>
                            <td colSpan={colSpan} className="no-data">Nenhum resultado encontrado</td>
                        </tr>
                    }
                    {!loading && rows.length > 0 &&
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                                {rows.map((row, index) => (
                                    row.id != null
                                        ? <SortableRow key={row.id} row={row} />
                                        : <StaticRow key={row.key ?? `static-${index}`} row={row} />
                                ))}
                            </SortableContext>
                        </DndContext>
                    }
                </tbody>
            </table>
        </div>
    );
};

export default SortableTable;
